import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, AlertTriangle, Users, Loader2, Phone, Check, ArrowLeft, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SubmitReportPage = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [caseType, setCaseType] = useState('missing');
  const [submittedCaseId, setSubmittedCaseId] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '', age: '', gender: 'male', height: '', complexion: '',
    identifying_marks: '', physical_details: '', clothing: '',
    city: '', state: '', last_seen_location: '', last_seen_date: '',
    last_seen_time: '', found_location: '', found_date: '', found_time: '',
    current_custody: '', belongings: '', reporter_name: '', reporter_mobile: '',
    reporter_email: '', reporter_relation: '', reporter_address: '',
    police_station: '', additional_info: ''
  });
  const [photo, setPhoto] = useState(null);
  const [firCopy, setFirCopy] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { fetchStates(); }, []);

  const fetchStates = async () => {
    try { const response = await axios.get(`${API}/api/states`); setStates(response.data); }
    catch (err) { console.error('Error fetching states:', err); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const formDataObj = new FormData();
      Object.keys(formData).forEach(key => { if (formData[key]) formDataObj.append(key, formData[key]); });
      if (photo) formDataObj.append('photo', photo);
      if (firCopy) formDataObj.append('fir_copy', firCopy);
      const endpoint = caseType === 'missing' ? '/api/submit/missing' : '/api/submit/unknown';
      const response = await axios.post(`${API}${endpoint}`, formDataObj, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmittedCaseId(response.data.case_id);
      setSuccess(true);
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.detail || 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।');
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setSuccess(false);
    setFormData({
      name: '', age: '', gender: 'male', height: '', complexion: '',
      identifying_marks: '', physical_details: '', clothing: '',
      city: '', state: '', last_seen_location: '', last_seen_date: '',
      last_seen_time: '', found_location: '', found_date: '', found_time: '',
      current_custody: '', belongings: '', reporter_name: '', reporter_mobile: '',
      reporter_email: '', reporter_relation: '', reporter_address: '',
      police_station: '', additional_info: ''
    });
    setPhoto(null); setPhotoPreview(null); setFirCopy(null);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="submit-success">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="app-card p-8 max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="font-outfit font-bold text-2xl text-gray-900 mb-2">रिपोर्ट सबमिट!</h2>
          <p className="text-gray-600 mb-6">आपकी रिपोर्ट सफलतापूर्वक दर्ज हो गई है। जल्द ही स्वीकृत की जाएगी।</p>
          <div className="bg-gray-100 p-4 rounded-2xl mb-6">
            <p className="text-sm text-gray-500">केस आईडी</p>
            <p className="font-mono font-bold text-lg text-gray-900">{submittedCaseId}</p>
          </div>
          <div className="space-y-3">
            <Button onClick={() => navigate('/')} className="w-full rounded-full bg-black">होम पर जाएं</Button>
            <Button onClick={resetForm} variant="outline" className="w-full rounded-full">नई रिपोर्ट</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const accentBg = caseType === 'missing' ? 'bg-red-600' : 'bg-amber-600';
  const accentHover = caseType === 'missing' ? 'hover:bg-red-700' : 'hover:bg-amber-700';

  return (
    <div className="pb-24 md:pb-12" data-testid="submit-report-page">
      {/* ===== MOBILE HEADER ===== */}
      <div className={`md:hidden ${accentBg} text-white p-6 pb-8 rounded-b-[2rem]`}>
        <div className="flex items-center gap-3 mb-4">
          <Link to="/"><Button variant="ghost" size="icon" className="bg-white/20 text-white hover:bg-white/30 rounded-full"><ArrowLeft className="w-5 h-5" /></Button></Link>
          <div>
            <h1 className="font-outfit font-bold text-xl">रिपोर्ट दर्ज करें</h1>
            <p className="text-white/70 text-sm">{caseType === 'missing' ? 'लापता व्यक्ति की जानकारी' : 'अज्ञात व्यक्ति की जानकारी'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCaseType('missing')} className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors ${caseType === 'missing' ? 'bg-white text-red-600' : 'bg-white/20 text-white'}`} data-testid="case-type-missing">
            <AlertTriangle className="w-4 h-4" />लापता
          </button>
          <button onClick={() => setCaseType('unknown')} className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors ${caseType === 'unknown' ? 'bg-white text-amber-600' : 'bg-white/20 text-white'}`} data-testid="case-type-unknown">
            <Users className="w-4 h-4" />अज्ञात
          </button>
        </div>
      </div>

      {/* ===== DESKTOP HEADER ===== */}
      <div className="hidden md:block py-8 border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-14 h-14 ${caseType === 'missing' ? 'bg-red-100' : 'bg-amber-100'} rounded-2xl flex items-center justify-center`}>
              <FileText className={`w-7 h-7 ${caseType === 'missing' ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <h1 className="font-outfit font-bold text-3xl text-gray-900">रिपोर्ट दर्ज करें</h1>
              <p className="text-gray-500">{caseType === 'missing' ? 'लापता व्यक्ति की जानकारी भरें' : 'अज्ञात व्यक्ति की जानकारी भरें'}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setCaseType('missing')} className={`px-6 py-3 rounded-xl font-medium text-sm flex items-center gap-2 transition-all border-2 ${caseType === 'missing' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'}`} data-testid="desktop-case-type-missing">
              <AlertTriangle className="w-4 h-4" />लापता व्यक्ति
            </button>
            <button onClick={() => setCaseType('unknown')} className={`px-6 py-3 rounded-xl font-medium text-sm flex items-center gap-2 transition-all border-2 ${caseType === 'unknown' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`} data-testid="desktop-case-type-unknown">
              <Users className="w-4 h-4" />अज्ञात व्यक्ति
            </button>
          </div>
        </div>
      </div>

      {/* Support Info - Mobile */}
      <div className="md:hidden px-4 -mt-4">
        <div className="app-card p-4 flex items-start gap-3 bg-blue-50 border-blue-200">
          <Phone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-900 text-sm">सहायता</p>
            <p className="text-xs text-blue-700">समस्या है? कॉल करें: <span className="font-bold">+91 7417074121</span></p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-4 md:px-0 mt-6 space-y-6 md:max-w-3xl md:mx-auto md:py-6">
        {/* Photo Upload */}
        <div className="app-card p-4 md:p-6">
          <Label className="font-outfit font-semibold mb-3 block">फोटो अपलोड करें</Label>
          <label className="block cursor-pointer">
            <div className={`aspect-square max-w-[200px] mx-auto rounded-2xl overflow-hidden border-2 border-dashed ${photoPreview ? 'border-green-400' : 'border-gray-300'} flex items-center justify-center bg-gray-50`}>
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center p-4">
                  <Camera className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">फोटो चुनें</p>
                  <p className="text-xs text-gray-400">JPG, PNG</p>
                </div>
              )}
            </div>
            <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" data-testid="input-photo" />
          </label>
        </div>

        {/* Person Info */}
        <div className="app-card p-4 md:p-6">
          <Label className="font-outfit font-semibold mb-4 block">व्यक्ति की जानकारी</Label>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm text-gray-600">पूरा नाम {caseType === 'missing' && '*'}</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder={caseType === 'unknown' ? 'अज्ञात (वैकल्पिक)' : 'व्यक्ति का नाम'} required={caseType === 'missing'} className="rounded-xl mt-1" data-testid="input-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="age" className="text-sm text-gray-600">उम्र *</Label>
                <Input id="age" name="age" type="number" value={formData.age} onChange={handleChange} placeholder="वर्ष" required className="rounded-xl mt-1" data-testid="input-age" />
              </div>
              <div>
                <Label htmlFor="gender" className="text-sm text-gray-600">लिंग *</Label>
                <Select value={formData.gender} onValueChange={(v) => handleSelectChange('gender', v)}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="select-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">पुरुष</SelectItem>
                    <SelectItem value="female">महिला</SelectItem>
                    <SelectItem value="other">अन्य</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="height" className="text-sm text-gray-600">ऊंचाई</Label>
                <Input id="height" name="height" value={formData.height} onChange={handleChange} placeholder="जैसे: 5.5 फीट" className="rounded-xl mt-1" data-testid="input-height" />
              </div>
              <div>
                <Label htmlFor="complexion" className="text-sm text-gray-600">रंग</Label>
                <Input id="complexion" name="complexion" value={formData.complexion} onChange={handleChange} placeholder="गोरा/सांवला" className="rounded-xl mt-1" data-testid="input-complexion" />
              </div>
            </div>
            <div>
              <Label htmlFor="identifying_marks" className="text-sm text-gray-600">पहचान चिह्न</Label>
              <Input id="identifying_marks" name="identifying_marks" value={formData.identifying_marks} onChange={handleChange} placeholder="कोई विशेष निशान" className="rounded-xl mt-1" data-testid="input-marks" />
            </div>
            <div>
              <Label htmlFor="clothing" className="text-sm text-gray-600">पहनावा</Label>
              <Input id="clothing" name="clothing" value={formData.clothing} onChange={handleChange} placeholder="अंतिम बार पहने कपड़े" className="rounded-xl mt-1" data-testid="input-clothing" />
            </div>
            <div>
              <Label htmlFor="physical_details" className="text-sm text-gray-600">अन्य शारीरिक विवरण</Label>
              <Input id="physical_details" name="physical_details" value={formData.physical_details} onChange={handleChange} placeholder="कोई अन्य विवरण" className="rounded-xl mt-1" data-testid="input-physical-details" />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="app-card p-4 md:p-6">
          <Label className="font-outfit font-semibold mb-4 block">{caseType === 'missing' ? 'अंतिम बार देखा गया' : 'पाया गया'}</Label>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-gray-600">स्थान *</Label>
              <Input name={caseType === 'missing' ? 'last_seen_location' : 'found_location'} value={caseType === 'missing' ? formData.last_seen_location : formData.found_location} onChange={handleChange} placeholder="पता" required className="rounded-xl mt-1" data-testid={caseType === 'missing' ? 'input-last-seen' : 'input-found-location'} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600">तारीख *</Label>
                <Input name={caseType === 'missing' ? 'last_seen_date' : 'found_date'} type="date" value={caseType === 'missing' ? formData.last_seen_date : formData.found_date} onChange={handleChange} required className="rounded-xl mt-1" data-testid={caseType === 'missing' ? 'input-last-date' : 'input-found-date'} />
              </div>
              <div>
                <Label className="text-sm text-gray-600">समय</Label>
                <Input name={caseType === 'missing' ? 'last_seen_time' : 'found_time'} type="time" value={caseType === 'missing' ? formData.last_seen_time : formData.found_time} onChange={handleChange} className="rounded-xl mt-1" data-testid={caseType === 'missing' ? 'input-last-time' : 'input-found-time'} />
              </div>
            </div>
            {caseType === 'unknown' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-gray-600">वर्तमान हिरासत</Label>
                  <Input name="current_custody" value={formData.current_custody} onChange={handleChange} placeholder="कहाँ है अभी" className="rounded-xl mt-1" data-testid="input-current-custody" />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">सामान</Label>
                  <Input name="belongings" value={formData.belongings} onChange={handleChange} placeholder="पास में मिला सामान" className="rounded-xl mt-1" data-testid="input-belongings" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="city" className="text-sm text-gray-600">शहर *</Label>
                <Input id="city" name="city" value={formData.city} onChange={handleChange} placeholder="शहर" required className="rounded-xl mt-1" data-testid="input-city" />
              </div>
              <div>
                <Label htmlFor="state" className="text-sm text-gray-600">राज्य *</Label>
                <Select value={formData.state} onValueChange={(v) => handleSelectChange('state', v)}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="select-state"><SelectValue placeholder="चुनें" /></SelectTrigger>
                  <SelectContent>{states.map(state => <SelectItem key={state} value={state}>{state}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Reporter Info */}
        <div className="app-card p-4 md:p-6">
          <Label className="font-outfit font-semibold mb-4 block">आपकी जानकारी</Label>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reporter_name" className="text-sm text-gray-600">आपका नाम *</Label>
              <Input id="reporter_name" name="reporter_name" value={formData.reporter_name} onChange={handleChange} placeholder="पूरा नाम" required className="rounded-xl mt-1" data-testid="input-reporter-name" />
            </div>
            <div>
              <Label htmlFor="reporter_mobile" className="text-sm text-gray-600">मोबाइल नंबर *</Label>
              <div className="flex mt-1">
                <span className="inline-flex items-center px-3 bg-gray-100 border border-r-0 border-gray-200 rounded-l-xl text-sm text-gray-600">+91</span>
                <Input id="reporter_mobile" name="reporter_mobile" value={formData.reporter_mobile} onChange={handleChange} placeholder="10 अंक" required pattern="[0-9]{10}" className="rounded-l-none rounded-r-xl" data-testid="input-reporter-mobile" />
              </div>
            </div>
            {caseType === 'missing' && (
              <div>
                <Label htmlFor="reporter_relation" className="text-sm text-gray-600">संबंध</Label>
                <Input id="reporter_relation" name="reporter_relation" value={formData.reporter_relation} onChange={handleChange} placeholder="जैसे: पिता, माता" className="rounded-xl mt-1" data-testid="input-reporter-relation" />
              </div>
            )}
            <div>
              <Label htmlFor="police_station" className="text-sm text-gray-600">पुलिस स्टेशन</Label>
              <Input id="police_station" name="police_station" value={formData.police_station} onChange={handleChange} placeholder="थाने का नाम" className="rounded-xl mt-1" data-testid="input-police-station" />
            </div>
            <div>
              <Label htmlFor="reporter_email" className="text-sm text-gray-600">ईमेल</Label>
              <Input id="reporter_email" name="reporter_email" type="email" value={formData.reporter_email} onChange={handleChange} placeholder="ईमेल (वैकल्पिक)" className="rounded-xl mt-1" data-testid="input-reporter-email" />
            </div>
            <div>
              <Label htmlFor="reporter_address" className="text-sm text-gray-600">पता</Label>
              <Input id="reporter_address" name="reporter_address" value={formData.reporter_address} onChange={handleChange} placeholder="आपका पूरा पता" className="rounded-xl mt-1" data-testid="input-reporter-address" />
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="app-card p-4 md:p-6">
          <Label className="font-outfit font-semibold mb-4 block">अतिरिक्त जानकारी</Label>
          <div className="space-y-4">
            <div>
              <Label htmlFor="additional_info" className="text-sm text-gray-600">कोई अन्य जानकारी</Label>
              <textarea id="additional_info" name="additional_info" value={formData.additional_info} onChange={handleChange} rows={3} placeholder="अन्य जानकारी जो मदद कर सके..." className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" data-testid="input-additional-info" />
            </div>
            <div>
              <Label className="text-sm text-gray-600">FIR कॉपी (वैकल्पिक)</Label>
              <Input type="file" accept=".pdf,.jpg,.png" onChange={(e) => setFirCopy(e.target.files[0])} className="rounded-xl mt-1" data-testid="input-fir-copy" />
            </div>
          </div>
        </div>

        {error && <div className="app-card p-4 bg-red-50 border-red-200 text-red-700 text-sm">{error}</div>}

        <Button type="submit" disabled={loading} className={`w-full h-14 rounded-full font-medium text-base ${accentBg} ${accentHover}`} data-testid="submit-report-btn">
          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />सबमिट हो रहा है...</> : <><FileText className="w-5 h-5 mr-2" />रिपोर्ट दर्ज करें</>}
        </Button>
      </form>
    </div>
  );
};

export default SubmitReportPage;
